import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import outputs from "amplify_outputs.json";
import { uploadData } from "aws-amplify/storage";
import type { Schema } from "schema/data/resource";
import { generateClient } from "aws-amplify/data";
import { DateUtils } from "../Utils/DateUtils";
import {
  DeepPartial,
  FileWithType,
  UploadResult,
  DOCUMENT_GROUPS,
} from "types";
import { fileVersioningService } from "./FileVersioningService";
import { UserContext } from "./utils/UserContext";
import { S3KeyResolver } from "./utils/S3KeyResolver";
import { DocumentTypeNormalizer } from "./utils/DocumentTypeNormalizer";
import { DocumentLinkManager } from "./managers/DocumentLinkManager";
import { DocumentVersionManager } from "./managers/DocumentVersionManager";
import { DomainResolver } from "./managers/DomainResolver";
import { DocumentUploadStrategy } from "./strategies/DocumentUploadStrategy";
import { LinkedDocumentUploadStrategy } from "./strategies/LinkedDocumentUploadStrategy";
import { UnlinkedDocumentUploadStrategy } from "./strategies/UnlinkedDocumentUploadStrategy";

const client = generateClient<Schema>({
  authMode: "userPool",
});

Amplify.configure(outputs);

const MAX_FILES_PER_BATCH = 5;
const BATCH_DELAY = 2000;

export const processBatch = async <T, R>(
  items: T[],
  processor: (item: T) => Promise<R | null>,
  onBatchStart: (batchNum: number, totalBatches: number) => void,
  batchSize = MAX_FILES_PER_BATCH,
  delay = BATCH_DELAY,
): Promise<{ results: R[]; totalProcessed: number }> => {
  const results: R[] = [];
  let totalProcessed = 0;
  const totalBatches = Math.ceil(items.length / batchSize);

  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const batchNum = Math.floor(i / batchSize) + 1;

    onBatchStart(batchNum, totalBatches);

    const batchPromises = batch.map(processor);
    const batchResults = await Promise.all(batchPromises);

    for (const result of batchResults) {
      if (result !== null) {
        results.push(result);
        totalProcessed++;
      }
    }

    if (i + batchSize < items.length) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  return { results, totalProcessed };
};

export interface FileProcessor {
  process(file: FileWithType): Promise<UploadResult | null>;
  extractData(input: { s3Key: string[]; fileName: string }): Promise<any>;
}

export abstract class AbstractFileProcessor implements FileProcessor {
  constructor(
    protected setStage: (fileName: string, stage: string) => void,
    protected setUploadProgress: (fileName: string, progress: number) => void,
    protected setExtractionProgress: (
      fileName: string,
      progress: number,
    ) => void,
    protected setUpdateProgress: (fileName: string, progress: number) => void,
    protected setSuccessMessage: (message: string) => void,
    protected setErrorMessage: (message: string) => void,
    protected handleUpdateUser: (
      data: any,
      uploadLinks: UploadResult[],
    ) => Promise<boolean>,
  ) {}

  abstract process(file: FileWithType): Promise<UploadResult | null>;

  protected async uploadFile(
    file: File,
    path: string,
    applicationId?: string,
  ): Promise<UploadResult> {
    const contentHash = await fileVersioningService.calculateContentHash(file);

    const uploadedData = await uploadData({
      path: ({ identityId }) =>
        `private/${identityId}/documents/${path}/${file.name}`,
      data: file,
      options: {
        onProgress: ({ transferredBytes, totalBytes }) => {
          if (totalBytes) {
            const progress = (transferredBytes / totalBytes) * 100;
            this.setUploadProgress(file.name, progress);
          }
        },
        metadata: {
          originalfilename: file.name,
          contenthash: contentHash,
          uploadedAt: new Date().toISOString(),
        },
      },
    }).result;

    const result: UploadResult = {
      path: uploadedData.path,
      fileName: file.name,
      contentHash,
      ...(applicationId && { applicationId }),
    };

    return result;
  }

  protected async createDocumentRecord(
    uploadResult: UploadResult,
    fileType: string,
    context?: {
      linkedEntityId?: string;
      linkedEntityType?: "APPLICATION" | "GENUINE_STUDENT";
      allowMultiple?: boolean;
      institutionId?: string;
    },
  ): Promise<void> {
    try {
      const userContext = await UserContext.fromSession();

      this.validateUploadContext(userContext, context);

      const documentType = DocumentTypeNormalizer.normalize(fileType);

      await new Promise((resolve) => setTimeout(resolve, 5000));

      const s3KeyResolver = new S3KeyResolver();
      const actualS3Key = await s3KeyResolver.resolveActualKey(
        uploadResult.path,
      );

      const fileName =
        uploadResult.fileName || uploadResult.path.split("/").pop() || "";

      const contentHash = uploadResult.contentHash || "";

      const strategy = this.createUploadStrategy(context);
      await strategy.execute(
        userContext,
        actualS3Key,
        fileName,
        documentType,
        contentHash,
      );

      uploadResult.path = actualS3Key;
    } catch (error) {
      throw error;
    }
  }

  private validateUploadContext(
    userContext: UserContext,
    context?: {
      linkedEntityId?: string;
      linkedEntityType?: "APPLICATION" | "GENUINE_STUDENT";
      allowMultiple?: boolean;
    },
  ): void {
    if (userContext.isInstitutionOrAgent && !context?.linkedEntityId) {
      throw new Error(
        `${userContext.userType} users must link documents to an application. ` +
          `Please provide applicationId when uploading documents.`,
      );
    }
  }

  private createUploadStrategy(context?: {
    linkedEntityId?: string;
    linkedEntityType?: "APPLICATION" | "GENUINE_STUDENT";
    allowMultiple?: boolean;
    institutionId?: string;
  }): DocumentUploadStrategy {
    const documentLinkManager = new DocumentLinkManager(client);
    const documentVersionManager = new DocumentVersionManager(client);
    const domainResolver = new DomainResolver(client);

    if (context?.linkedEntityId) {
      return new LinkedDocumentUploadStrategy(
        client,
        {
          linkedEntityId: context.linkedEntityId,
          linkedEntityType: context.linkedEntityType!,
          allowMultiple: context.allowMultiple,
          institutionId: context.institutionId,
        },
        documentLinkManager,
        documentVersionManager,
        domainResolver,
      );
    }

    return new UnlinkedDocumentUploadStrategy(client, documentVersionManager);
  }

  public async extractData(input: {
    s3Key: string[];
    fileName: string;
  }): Promise<any> {
    const fileName = input.fileName;

    try {
      this.setStage(fileName, "extracting");
      this.setExtractionProgress(fileName, 5);

      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      const identityId = session.identityId;

      this.setExtractionProgress(fileName, 15);

      const extractResponse: any = await client.queries.extractResumeInfo(
        {
          identityId: identityId,
          s3Key: input.s3Key[0],
        } as any,
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      this.setExtractionProgress(fileName, 25);

      const responseStr = extractResponse.data;

      if (!responseStr) {
        throw new Error("Empty response from Lambda");
      }

      for (let i = 30; i <= 90; i += 15) {
        this.setExtractionProgress(fileName, i);
        await new Promise((resolve) => setTimeout(resolve, 150));
      }

      let response;

      const bodyStart = responseStr.indexOf("body={");
      if (bodyStart !== -1) {
        const bodyJson = responseStr.substring(
          bodyStart + 5,
          responseStr.length - 1,
        );

        try {
          const body = JSON.parse(bodyJson);
          const statusMatch = responseStr.match(/statusCode=(\d+)/);
          const statusCode = statusMatch ? parseInt(statusMatch[1]) : 200;

          response = {
            statusCode: statusCode,
            body: body,
          };
        } catch (parseError) {
          this.setStage(fileName, "failed");
          throw new Error(
            `Failed to parse Lambda response: ${
              parseError instanceof Error
                ? parseError.message
                : String(parseError)
            }`,
          );
        }
      } else {
        this.setStage(fileName, "failed");
        throw new Error("Could not find body in response");
      }

      if (response.statusCode !== 200) {
        const errorMsg = response.body?.message || "Data extraction failed";

        if (
          response.statusCode === 500 &&
          errorMsg.includes("ThrottlingException")
        ) {
          throw new Error(
            "AI service is busy. Please wait a few minutes and try with fewer files.",
          );
        }

        throw new Error(`Lambda error (${response.statusCode}): ${errorMsg}`);
      }

      this.setExtractionProgress(fileName, 100);
      this.setStage(fileName, "complete");

      return response.body.content;
    } catch (error) {
      this.setStage(fileName, "failed");
      this.setErrorMessage(
        `Failed to extract data from ${fileName}: ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
      throw error;
    }
  }
}

export class GenericFileProcessor extends AbstractFileProcessor {
  private folderPath: string;
  private documentContext?: {
    linkedEntityId?: string;
    linkedEntityType?: "APPLICATION" | "GENUINE_STUDENT";
    allowMultiple?: boolean;
  };

  constructor(
    folderPath: string,
    setStage: (fileName: string, stage: string) => void,
    setUploadProgress: (fileName: string, progress: number) => void,
    setExtractionProgress: (fileName: string, progress: number) => void,
    setUpdateProgress: (fileName: string, progress: number) => void,
    setSuccessMessage: (message: string) => void,
    setErrorMessage: (message: string) => void,
    handleUpdateUser: (
      data: any,
      uploadLinks: UploadResult[],
    ) => Promise<boolean>,
    documentContext?: {
      linkedEntityId?: string;
      linkedEntityType?: "APPLICATION" | "GENUINE_STUDENT";
      allowMultiple?: boolean;
    },
  ) {
    super(
      setStage,
      setUploadProgress,
      setExtractionProgress,
      setUpdateProgress,
      setSuccessMessage,
      setErrorMessage,
      handleUpdateUser,
    );
    this.folderPath = folderPath;
    this.documentContext = documentContext;
  }

  async process(file: FileWithType): Promise<UploadResult | null> {
    try {
      this.setStage(file.file.name, "uploading");
      const result = await this.uploadFile(
        file.file,
        this.folderPath,
        file?.applicationId,
      );

      await this.createDocumentRecord(
        result,
        file.fileType,
        this.documentContext,
      );

      this.setStage(file.file.name, "uploaded");
      return result;
    } catch (error) {
      this.setErrorMessage(
        error instanceof Error ? error.message : String(error),
      );
      this.setStage(file.file.name, "failed");
      return null;
    }
  }
}

export class UnsupportedFileProcessor extends AbstractFileProcessor {
  async process(file: FileWithType): Promise<UploadResult | null> {
    this.setStage(file.file.name, "failed");
    this.setErrorMessage(
      `Processing of ${file.fileType} is currently unsupported.`,
    );
    return null;
  }
}

export class FileProcessorFactory {
  private static fileTypeMap: Record<string, string> = {};

  static registerFileType(fileType: string, folderPath: string): void {
    this.fileTypeMap[fileType] = folderPath;
  }

  static createProcessor(
    fileType: string,
    setStage: (fileName: string, stage: string) => void,
    setUploadProgress: (fileName: string, progress: number) => void,
    setExtractionProgress: (fileName: string, progress: number) => void,
    setUpdateProgress: (fileName: string, progress: number) => void,
    setSuccessMessage: (message: string) => void,
    setErrorMessage: (message: string) => void,
    handleUpdateUser: (
      data: any,
      uploadLinks: UploadResult[],
    ) => Promise<boolean>,
    documentContext?: {
      linkedEntityId?: string;
      linkedEntityType?: "APPLICATION" | "GENUINE_STUDENT";
      allowMultiple?: boolean;
    },
  ): FileProcessor {
    if (this.fileTypeMap[fileType]) {
      return new GenericFileProcessor(
        this.fileTypeMap[fileType],
        setStage,
        setUploadProgress,
        setExtractionProgress,
        setUpdateProgress,
        setSuccessMessage,
        setErrorMessage,
        handleUpdateUser,
        documentContext,
      );
    }

    return new UnsupportedFileProcessor(
      setStage,
      setUploadProgress,
      setExtractionProgress,
      setUpdateProgress,
      setSuccessMessage,
      setErrorMessage,
      handleUpdateUser,
    );
  }
}

const registerDocumentTypes = () => {
  Object.values(DOCUMENT_GROUPS).forEach((group) => {
    group.types.forEach((type) => {
      FileProcessorFactory.registerFileType(
        type.toLowerCase().replace(/_/g, ""),
        group.folder,
      );
    });
  });
};

registerDocumentTypes();

export const removeTypenameAndNullSections = <T extends object>(
  obj: T,
  allowedSet?: Set<string>,
  allowedKeys?: Record<string, Set<string>>,
): DeepPartial<T> => {
  if (obj === null || typeof obj !== "object") {
    return obj as DeepPartial<T>;
  }

  try {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (
        key === "id" ||
        (key !== "__typename" &&
          value !== null &&
          value !== undefined &&
          value !== "" &&
          (allowedSet === undefined || allowedSet.has(key)))
      ) {
        if (Array.isArray(value)) {
          const cleanedArray = value
            .map((item) =>
              typeof item === "object" && item !== null
                ? removeTypenameAndNullSections(
                    item as object,
                    allowedKeys ? allowedKeys[key] : undefined,
                    allowedKeys,
                  )
                : item,
            )
            .filter(
              (item) => item !== null && item !== undefined && item !== "",
            );

          if (cleanedArray.length > 0) {
            (acc as any)[key] = cleanedArray;
          }
        } else if (typeof value === "object") {
          const cleanedValue = removeTypenameAndNullSections(
            value as object,
            allowedKeys ? allowedKeys[key] : undefined,
            allowedKeys,
          );
          if (Object.keys(cleanedValue).length > 0) {
            (acc as any)[key] = cleanedValue;
          }
        } else {
          (acc as any)[key] = value;
        }
      }
      return acc;
    }, {} as DeepPartial<T>);
  } catch (error) {
    console.error("Error in removeTypenameAndNullSections:", {
      error,
      obj,
      allowedSet: allowedSet ? Array.from(allowedSet) : undefined,
      allowedKeys: allowedKeys ? Object.keys(allowedKeys) : undefined,
    });
    throw error;
  }
};

export function mergeSection<T extends object>(
  existingSection: DeepPartial<T> | undefined,
  newSection: DeepPartial<T> | undefined,
): DeepPartial<T> {
  if (!existingSection && !newSection) return {} as DeepPartial<T>;
  if (!existingSection) return newSection as DeepPartial<T>;
  if (!newSection) return existingSection;

  try {
    const result: any = { ...existingSection };

    for (const [key, newValue] of Object.entries(newSection)) {
      if (newValue === null || newValue === undefined || newValue === "") {
        continue;
      }

      if (key in result) {
        const existingValue = result[key];

        if (
          typeof existingValue === "object" &&
          typeof newValue === "object" &&
          existingValue !== null &&
          newValue !== null &&
          !Array.isArray(existingValue) &&
          !Array.isArray(newValue)
        ) {
          const hasContent = Object.keys(newValue).length > 0;
          if (hasContent) {
            result[key] = mergeSection(existingValue, newValue);
          }
        } else {
          if (
            existingValue === null ||
            existingValue === undefined ||
            existingValue === ""
          ) {
            result[key] = newValue;
          }
        }
      } else {
        result[key] = newValue;
      }
    }

    return result as DeepPartial<T>;
  } catch (error) {
    console.error("Error in mergeSection:", {
      error,
      existingSection,
      newSection,
    });
    throw error;
  }
}

export function mergeExtractedData<T extends Record<string, any>>(
  existingData: Record<string, any>,
  newData: T,
): Record<string, any> {
  const result: Record<string, any> = { ...existingData };

  for (const [key, newValue] of Object.entries(newData)) {
    if (newValue === null || newValue === undefined || newValue === "") {
      continue;
    }

    if (key in result) {
      const existingValue = result[key];

      if (
        typeof existingValue === "object" &&
        typeof newValue === "object" &&
        existingValue !== null &&
        newValue !== null &&
        !Array.isArray(existingValue) &&
        !Array.isArray(newValue)
      ) {
        const hasContent = Object.keys(newValue).length > 0;
        if (hasContent) {
          result[key] = mergeSection(existingValue, newValue);
        }
      } else {
        if (
          existingValue === null ||
          existingValue === undefined ||
          existingValue === ""
        ) {
          result[key] = newValue;
        }
      }
    } else {
      result[key] = newValue;
    }
  }

  return result;
}

export function convertDatesInSection(section: any): any {
  try {
    return DateUtils.convertDatesInObject(section);
  } catch (error) {
    console.error("Error in convertDatesInSection:", {
      error,
      section,
    });
    throw error;
  }
}
